import { useEffect, useState } from 'react';
import type { ArticleCard } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import useRegion from '~/hooks/useRegion';
import { formatArticleCardDate } from '~/utils/dates';

export type MetadataSize = 'sm' | 'md' | 'lg';
export type MetadataTheme = 'light' | 'dark';
export type TitleLines = 1 | 2 | 3 | 4;

type Props = {
  expanded?: boolean;
  hideCta?: boolean;
  isTextOverImage?: boolean;
  item: ArticleCard;
  showSection?: boolean;
  size: MetadataSize;
  theme: MetadataTheme;
  titleLines?: TitleLines;
};

const TITLE_SIZES: Record<MetadataSize, string> = {
  lg: 'text-card-lg',
  md: 'text-card-md',
  sm: 'text-card-sm'
};

const TITLE_CLAMPS: Record<TitleLines, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4'
};

const LINES_WITH_EXCERPT = 3;
const LINES_WITHOUT_EXCERPT = 4;
const READ_LABEL = 'Read more';

export default function Metadata({
  expanded,
  hideCta,
  isTextOverImage = false,
  item,
  showSection,
  size,
  theme,
  titleLines
}: Props) {
  const lines =
    titleLines ?? (expanded ? LINES_WITH_EXCERPT : LINES_WITHOUT_EXCERPT);
  const date = item.modified_at || item.published_at;

  return (
    <>
      <div className="flex flex-col gap-1">
        {showSection && (
          <h4 className="text-start text-xs text-grey-hide">
            {item.section ?? 'News'}
          </h4>
        )}
        <h3
          className={`text-start ${TITLE_SIZES[size]} ${
            theme === 'dark' ? 'text-white' : 'text-black'
          } ${TITLE_CLAMPS[lines]}`}
        >
          {item.title}
        </h3>
      </div>

      {expanded && item.excerpt && (
        <p
          className={`${size === 'sm' ? '' : 'mt-1'} line-clamp-4 text-sm ${
            theme === 'dark' ? 'text-white' : 'text-grey-hide'
          }`}
        >
          {item.excerpt}
        </p>
      )}

      {!hideCta && (
        <div
          className={`${
            size === 'sm' ? '' : 'mt-1'
          } flex w-full flex-row items-center gap-1 text-nowrap text-xs ${
            isTextOverImage ? 'text-grey-divide' : 'text-grey-hide'
          }`}
        >
          <p className="leading-none">{READ_LABEL}</p>
          <div className="flex translate-y-px rotate-270 items-center justify-center">
            <ChevronIcon
              stroke={isTextOverImage ? '#CFCFCF' : '#A9A9A9'}
              width="8px"
              height="8px"
            />
          </div>
          {date ? <CardDate timestamp={date} /> : null}
        </div>
      )}
    </>
  );
}

function CardDate({ timestamp }: { timestamp: number }) {
  const region = useRegion();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => setNow(Date.now()), []);

  return (
    <p className="ml-auto leading-none" suppressHydrationWarning>
      {formatArticleCardDate(timestamp, region, now)}
    </p>
  );
}
